import { ContactSchema } from '@/schemas/broadcast';
import { AlertCircle, CheckCircle2, Edit2, Trash2, X } from 'lucide-react';
import { useState } from 'react';

/**
 * ContactPreviewTable - Displays parsed contacts with validation and editing
 */
const ContactPreviewTable = ({ contacts, onUpdate, onRemove }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValues, setEditValues] = useState({ name: '', phoneNumber: '' });

  const handleStartEdit = (index, contact) => {
    setEditingIndex(index);
    setEditValues({ name: contact.name, phoneNumber: contact.phoneNumber });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValues({ name: '', phoneNumber: '' });
  };

  const handleSaveEdit = index => {
    onUpdate(index, editValues);
    setEditingIndex(null);
  };

  const validateItem = contact => {
    const result = ContactSchema.safeParse(contact);
    if (result.success) return { isValid: true, errors: {} };

    // Flatten errors by field
    const fieldErrors = {};
    result.error.issues.forEach(issue => {
      fieldErrors[issue.path[0]] = issue.message;
    });
    return { isValid: false, errors: fieldErrors };
  };

  if (!contacts || contacts.length === 0) {
    return (
      <div className="p-8 text-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
        <p className="text-sm text-gray-500">No contacts imported yet.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                Phone Number
              </th>
              <th className="px-4 py-3 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contacts.map((contact, index) => {
              const { isValid, errors } = validateItem(contact);
              const isEditing = editingIndex === index;

              return (
                <tr
                  key={index}
                  className={`${!isValid ? 'bg-red-50/30' : 'hover:bg-gray-50/80'} transition-colors`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    {isValid ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {isEditing ? (
                      <div>
                        <input
                          type="text"
                          value={editValues.name}
                          onChange={e =>
                            setEditValues(prev => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          className="w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-primary-500 outline-none"
                        />
                        {errors.name && (
                          <p className="text-[10px] text-red-600 mt-1">
                            {errors.name}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-900">
                        {contact.name}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {isEditing ? (
                      <div>
                        <input
                          type="text"
                          value={editValues.phoneNumber}
                          onChange={e =>
                            setEditValues(prev => ({
                              ...prev,
                              phoneNumber: e.target.value,
                            }))
                          }
                          className="w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-primary-500 outline-none"
                        />
                        {errors.phoneNumber && (
                          <p className="text-[10px] text-red-600 mt-1">
                            {errors.phoneNumber}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span
                        className={`text-sm ${!isValid && errors.phoneNumber ? 'text-red-700 font-medium' : 'text-gray-600'}`}
                      >
                        {contact.phoneNumber}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end items-center space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleSaveEdit(index)}
                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => handleStartEdit(index, contact)}
                            className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => onRemove(index)}
                            className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Showing {contacts.length}{' '}
          {contacts.length === 1 ? 'contact' : 'contacts'}.
          {contacts.some(c => !ContactSchema.safeParse(c).success) && (
            <span className="ml-2 text-red-600 font-semibold italic">
              Some contacts require attention.
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

export default ContactPreviewTable;
